import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../config';
import './ClientForm.css';

function ClientForm({ client, onClose }) {
  const [formData, setFormData] = useState({
    name: '',
    service_type: '',
    linehaul_or_pad: '',
    phone_number: '',
    company: '',
    email: '',
    location: '',
    current_status: '',
    status: '',
    call_date: '',
    call_notes: '',
    onboarding_date: '',
    recruiting_start_tasks: '',
    current_provider: '',
    state: '',
    city: '',
    station: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [companies, setCompanies] = useState([]);
  const [stations, setStations] = useState([]);
  const [showNewCompany, setShowNewCompany] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [addingCompany, setAddingCompany] = useState(false);

  useEffect(() => {
    if (client) {
      setFormData(client);
    }
    fetchCompanies();
    fetchStations();
  }, [client]);

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(res.data);
    } catch (err) {
      console.error('Error fetching companies:', err);
    }
  };

  const fetchStations = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/stations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStations(res.data);
    } catch (err) {
      console.error('Error fetching stations:', err);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCreateCompany = async () => {
    if (!newCompanyName.trim()) return;
    setAddingCompany(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/api/companies`, { name: newCompanyName.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const created = res.data;
      const updated = [...companies, created].sort((a, b) => a.name.localeCompare(b.name));
      setCompanies(updated);
      setFormData({ ...formData, company: created.name });
      setNewCompanyName('');
      setShowNewCompany(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Error creating company');
    } finally {
      setAddingCompany(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      if (client) {
        await axios.put(`${API_URL}/api/clients/${client.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_URL}/api/clients`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Error saving client');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{client ? 'Edit Client' : 'Add New Client'}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Service Type</label>
              <input
                type="text"
                name="service_type"
                value={formData.service_type}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Linehaul or P&D</label>
              <select
                name="linehaul_or_pad"
                value={formData.linehaul_or_pad}
                onChange={handleChange}
              >
                <option value="">Select...</option>
                <option value="Linehaul">Linehaul</option>
                <option value="P&D">P&D</option>
              </select>
            </div>
            <div className="form-group">
              <label>Phone Number</label>
              <input
                type="tel"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Company</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flexDirection: 'column' }}>
                <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                  <select
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    style={{ flex: 1 }}
                  >
                    <option value="">-- Select Company --</option>
                    {companies.map(c => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowNewCompany(!showNewCompany)}
                    className="btn-secondary"
                    style={{ whiteSpace: 'nowrap', padding: '6px 12px', fontSize: '13px' }}
                  >
                    + New
                  </button>
                </div>
                {showNewCompany && (
                  <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                    <input
                      type="text"
                      placeholder="New company name"
                      value={newCompanyName}
                      onChange={(e) => setNewCompanyName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleCreateCompany())}
                      style={{ flex: 1 }}
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={handleCreateCompany}
                      disabled={addingCompany}
                      className="btn-primary"
                      style={{ whiteSpace: 'nowrap', padding: '6px 12px', fontSize: '13px' }}
                    >
                      {addingCompany ? '...' : 'Add'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowNewCompany(false); setNewCompanyName(''); }}
                      className="btn-secondary"
                      style={{ padding: '6px 12px', fontSize: '13px' }}
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Location</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Current Status</label>
              <input
                type="text"
                name="current_status"
                value={formData.current_status}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="">Select...</option>
                <option value="Lead">Lead</option>
                <option value="Closed">Closed</option>
                <option value="Onboard">Onboard</option>
                <option value="NRLV">NRLV</option>
              </select>
            </div>
            <div className="form-group">
              <label>Call Date</label>
              <input
                type="date"
                name="call_date"
                value={formData.call_date}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Call Notes</label>
            <textarea
              name="call_notes"
              value={formData.call_notes}
              onChange={handleChange}
              rows="3"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Onboarding Date</label>
              <input
                type="date"
                name="onboarding_date"
                value={formData.onboarding_date}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Recruiting Start/Tasks</label>
            <textarea
              name="recruiting_start_tasks"
              value={formData.recruiting_start_tasks}
              onChange={handleChange}
              rows="3"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Current Provider</label>
              <input
                type="text"
                name="current_provider"
                value={formData.current_provider}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Station</label>
              <select
                name="station"
                value={formData.station || ''}
                onChange={handleChange}
              >
                <option value="">-- Select Station --</option>
                {stations.map(s => (
                  <option key={s.id} value={s.name}>{s.name}</option>
                ))}
                {formData.station && !stations.find(s => s.name === formData.station) && (
                  <option value={formData.station}>{formData.station}</option>
                )}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>State</label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>City</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
              />
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ClientForm;
